import React, { useEffect, useState } from "react";
import {
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Divider,
  Button,
  Typography,
  Spin,
  List,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch } from "react-redux";
import { setIsShowFilter } from "shared/redux/payout-reducer";
import "./index.scss";
import "./payout.scss";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title } = Typography;

type Props = {
  onApplyFilter: (values: any) => void;
  filteredValues: any;
  loadingDropdown: boolean;
  payoutStatus: any;
  payoutFilterValues: {
    lenders: Array<{ key: string; value: string }>;
    businessNames: Array<{ key: string; value: string }>;
    loanProducts: Array<{ key: string; value: string }>;
  };
};

const PayoutFilter: React.FunctionComponent<Props> = ({
  onApplyFilter,
  filteredValues,
  payoutFilterValues,
  payoutStatus,
  loadingDropdown,
}) => {
  const [lender, setLender] = useState<string | null>(filteredValues?.lenderId);
  const [product, setProduct] = useState<string | null>(
    filteredValues?.productId
  );
  const [businessName, setBusinessName] = useState<string | null>(
    filteredValues?.connectorBusinessId
  );
  const [disbursedDate, setDisbursedDate] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null);
  const [reqDate, setReqDate] = useState<
    [moment.Moment | null, moment.Moment | null] | null
  >(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (filteredValues?.disbursedDateFrom && filteredValues?.disbursedDateTo) {
      setDisbursedDate([
        moment(filteredValues?.disbursedDateFrom, "YYYY-MM-DD"),
        moment(filteredValues?.disbursedDateTo, "YYYY-MM-DD"),
      ]);
    }
    if (
      filteredValues?.payoutRequestedDateFrom &&
      filteredValues?.payoutRequestedDateTo
    ) {
      setReqDate([
        moment(filteredValues?.payoutRequestedDateFrom, "YYYY-MM-DD"),
        moment(filteredValues?.payoutRequestedDateTo, "YYYY-MM-DD"),
      ]);
    }
  }, []);

  const handleCancel = () => {
    setLender(null);
    setProduct(null);
    setBusinessName(null);
    setDisbursedDate(null);
    setReqDate(null);
    onApplyFilter({
      lenderId: null,
      connectorBusinessId: null,
      productId: null,
      disbursedDateFrom: null,
      disbursedDateTo: null,
      payoutRequestedDateFrom: null,
      payoutRequestedDateTo: null,
      isCancel: true,
    });
  };

  const handleApplyFilterClick = () => {
    onApplyFilter({
      lenderId: lender,
      connectorBusinessId: businessName,
      productId: product,
      disbursedDateFrom: disbursedDate
        ? disbursedDate?.at(0)?.format("YYYY-MM-DD")
        : null,
      disbursedDateTo: disbursedDate
        ? disbursedDate?.at(1)?.format("YYYY-MM-DD")
        : null,
      payoutRequestedDateFrom: reqDate
        ? reqDate?.at(0)?.format("YYYY-MM-DD")
        : null,
      payoutRequestedDateTo: reqDate
        ? reqDate?.at(1)?.format("YYYY-MM-DD")
        : null,
    });
  };

  const listHierarchy = [
    <List.Item key="LENDER">
      <Select
        placeholder="Lender Name"
        className="custom-select"
        onSelect={(value: string) => setLender(value)}
        value={lender}
        showSearch
        optionFilterProp="children"
        disabled={!["DISBURSED", "REQUESTED", "APPROVED"].includes(payoutStatus)}
      >
        {payoutFilterValues?.lenders
          ?.sort(function (a: any, b: any) {
            if (a.value?.toLowerCase() < b.value?.toLowerCase()) return -1;
            if (a.value?.toLowerCase() > b.value?.toLowerCase()) return 1;
            return 0;
          })
          ?.map((lender, i) => (
            <Option key={lender.key + i} value={lender.key}>
              {lender.value}
            </Option>
          ))}
      </Select>
    </List.Item>,
    <List.Item key="PRODUCT">
      <Select
        placeholder="Product"
        className="custom-select"
        onChange={(value: string) => setProduct(value)}
        value={product}
        showSearch
        optionFilterProp="children"
        disabled={!["DISBURSED", "REQUESTED", "APPROVED"].includes(payoutStatus)}
      >
        {payoutFilterValues?.loanProducts
          ?.sort(function (a: any, b: any) {
            if (a.value?.toLowerCase() < b.value?.toLowerCase()) return -1;
            if (a.value?.toLowerCase() > b.value?.toLowerCase()) return 1;
            return 0;
          })
          ?.map((product, i) => (
            <Option key={product.key + i} value={product.key}>
              {product.value}
            </Option>
          ))}
      </Select>
    </List.Item>,
    <List.Item key="CONNECTOR">
      <Select
        placeholder="Connector Business Name"
        className="custom-select"
        onChange={(value: string) => setBusinessName(value)}
        value={businessName}
        showSearch
        optionFilterProp="children"
        disabled={["INVOICES"].includes(payoutStatus)}
      >
        {payoutFilterValues?.businessNames
          ?.sort(function (a: any, b: any) {
            if (a.value?.toLowerCase() < b.value?.toLowerCase()) return -1;
            if (a.value?.toLowerCase() > b.value?.toLowerCase()) return 1;
            return 0;
          })
          ?.map((business, i) => (
            <Option key={business.key + i} value={business.key}>
              {business.value}
            </Option>
          ))}
      </Select>
    </List.Item>,
    <List.Item key="DIS_DATE">
      <RangePicker
        className="custom-input"
        placeholder={["Disbursed From Date", "Disbursed To Date"]}
        value={disbursedDate}
        onChange={(
          dates: [moment.Moment | null, moment.Moment | null] | null
        ) => setDisbursedDate(dates)}
        disabledDate={(current) => current && current > moment().endOf("day")}
        disabled={!["DISBURSED", "REQUESTED", "APPROVED"].includes(payoutStatus)}
      />
    </List.Item>,
    <List.Item key="REQ_DATE">
      <RangePicker
        className="custom-input"
        placeholder={["Req From Date", "Req To Date"]}
        value={reqDate}
        onChange={(
          dates: [moment.Moment | null, moment.Moment | null] | null
        ) => setReqDate(dates)}
        disabledDate={(current) => current && current > moment().endOf("day")}
        disabled={!["APPROVED"].includes(payoutStatus)}
      />
    </List.Item>,
  ];

  return (
    <Card style={{ width: "85%", marginBottom: "30px" }}>
      <Title level={5} style={{ marginLeft: "5px", marginBottom: "-18px" }}>
        <Row>
          <Col span={12}>Filter</Col>
          <Col span={12} style={{ display: "flex", justifyContent: "end" }}>
            <CloseOutlined onClick={() => dispatch(setIsShowFilter(false))} />
          </Col>
        </Row>
      </Title>
      <Divider />
      <Spin spinning={loadingDropdown}>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <List
            style={{ marginTop: "8px" }}
            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 }}
            dataSource={listHierarchy}
            renderItem={(item) => item}
          />
        </div>
        <Divider />
        <div style={{ justifyContent: "right", gap: "10px", display: "flex" }}>
          <Button size="large" htmlType="button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="button"
            onClick={handleApplyFilterClick}
          >
            Apply Filter
          </Button>
        </div>
      </Spin>
    </Card>
  );
};

export default PayoutFilter;
